<template>
  <v-dialog
    v-model="isOpen"
    max-width="900px"
    persistent
    scrollable
    @keydown.esc="closeDialog"
  >
    <v-card class="report_govem_pdf-viewer-dialog">
      <!-- Dialog Header -->
      <v-card-title class="dialog-header">
        <span>{{ report_govem_pdf.filename }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeDialog" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Dialog Content -->
      <v-card-text class="dialog-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular
            indeterminate
            color="primary"
            size="60"
          ></v-progress-circular>
          <p class="mt-4">กำลังสร้างเกียรติบัตร...</p>
        </div>

        <!-- PDF Viewer -->
        <div v-else-if="report_govem_pdf.pdfUrl" class="pdf-viewer-wrapper">   
          <div class="pdf-container">
            <iframe
              :src="'http://localhost:8080/HRvec_api/exports/' + report_govem_pdf.pdfUrl"
              class="pdf-iframe"
              title="Certificate PDF Viewer"
            ></iframe>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="errorMessage" class="error-container">
          <v-alert type="error" dense class="mb-4">
            {{ errorMessage }}
          </v-alert>
          <p class="mt-2">
            ไม่สามารถสร้างเกียรติบัตร PDF ได้ กรุณาลองใหม่อีกครั้ง
          </p>
        </div>

        <!-- Certificate Info -->
        <div class="report_govem_pdf-info">
          <v-simple-table>
            <tbody>
              <tr>
                <td><strong>รหัสเกียรติบัตร:</strong></td>
                <td>{{ report_govem_pdf.filename }}</td>
              </tr>
              <tr>
                <td><strong>สถานะ:</strong></td>
                <td>
                  <v-chip
                    :color="getStatusColor(report_govem_pdf.isNew)"
                    text-color="white"
                    small
                  >
                    {{
                      getStatusText(report_govem_pdf.report_govem_pdf_Status)
                    }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td><strong>วันที่ออก:</strong></td>
                <td>
                  {{ formatDate(report_govem_pdf.report_govem_pdf_IssueDate) }}
                </td>
              </tr>
              <tr v-if="report_govem_pdf.report_govem_pdf_ExpiryDate">
                <td><strong>วันหมดอายุ:</strong></td>
                <td>
                  {{ formatDate(report_govem_pdf.report_govem_pdf_ExpiryDate) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>

      <!-- Dialog Actions -->
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog" :disabled="downloading">
          ปิด
        </v-btn>
        <v-btn
          color="primary"
          @click="downloadPdf"
          :loading="downloading"
          :disabled="!pdfUrl"
        >
          ดาวน์โหลด
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "CertificateViewer",
  props: {
    report_govem_pdf: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isOpen: true,
      loading: false,
      downloading: false,
      pdfUrl: "",
      errorMessage: "",
      currentPage: 1,
      totalPages: 1,
      apiKey: process.env.VUE_APP_API_KEY || "ovecktc2025",
      maxRetries: 3,
      retryCount: 0
    };
  },
  watch: {
    report_govem_pdf: {
      handler() {
        this.retryCount = 0;
        this.generatePdf();
      },
      deep: true
    }
  },
  mounted() {},
  methods: {
    /**
     * ดาวน์โหลด PDF
     */
    async downloadPdf() {
      if (!this.report_govem_pdf.pdfUrl) {
        this.errorMessage = "ยังไม่มี PDF ให้ดาวน์โหลด";
        return;
      }

      this.downloading = true;
      try {
        const link = document.createElement("a");
        link.href = this.report_govem_pdf.pdfUrl;
        link.download = `report_govem_pdf_${this.report_govem_pdf.filename}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Log download access
        this.logCertificateAccess("download");
      } catch (error) {
        console.error("Download Error:", error);
        this.errorMessage = "เกิดข้อผิดพลาดในการดาวน์โหลด กรุณาลองใหม่";
      } finally {
        this.downloading = false;
      }
    },

    /**
     * พิมพ์ PDF
     */
    printPdf() {
      if (!this.report_govem_pdf.pdfUrl) {
        this.errorMessage = "ยังไม่มี PDF ให้พิมพ์";
        return;
      }

      try {
        const printWindow = window.open(this.report_govem_pdf.pdfUrl, "_blank");
        if (printWindow) {
          printWindow.addEventListener("load", () => {
            printWindow.print();
          });
        } else {
          throw new Error("ไม่สามารถเปิดหน้าต่างพิมพ์ได้");
        }

        // Log print access
        this.logCertificateAccess("print");
      } catch (error) {
        console.error("Print Error:", error);
        this.errorMessage = "เกิดข้อผิดพลาดในการพิมพ์ กรุณาลองใหม่";
      }
    },

    /**
     * ไปหน้าถัดไป
     */
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    /**
     * ไปหน้าก่อนหน้า
     */
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    /**
     * ปิด Dialog
     */
    closeDialog() {
      this.isOpen = false;
      this.$emit("close");
    },

    /**
     * แปลงสี state เป็นสี component
     */
    getStatusColor(status) {
      const colors = {
        pending: "orange",
        issued: "green",
        revoked: "red"
      };
      return colors[status] || "grey";
    },

    /**
     * แปลง status เป็นข้อความไทย
     */
    getStatusText(status) {
      const texts = {
        pending: "รอการออก",
        issued: "ออกแล้ว",
        revoked: "ยกเลิก"
      };
      return texts[status] || status;
    },

    /**
     * แปลงวันที่เป็นรูปแบบการแสดงผล
     */
    formatDate(dateString) {
      if (!dateString) return "-";

      try {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        };
        return new Date(dateString).toLocaleDateString("th-TH", options);
      } catch (error) {
        console.error("Date format error:", error);
        return dateString;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.report_govem_pdf-viewer-dialog {
  .dialog-header {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    color: white;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .close-btn {
      color: white;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .dialog-content {
    padding: 24px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .dialog-actions {
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #64748b;
}

.error-container {
  padding: 20px;
  text-align: center;
  color: #dc2626;

  p {
    margin: 12px 0;
    font-size: 14px;
  }
}

.pdf-viewer-wrapper {
  margin-bottom: 24px;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;

  .page-info {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
  }
}

.pdf-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pdf-iframe {
  width: 100%;
  height: 600px;
  border: none;
}

.report_govem_pdf-info {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;

  table {
    width: 100%;
    font-size: 14px;

    tr {
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }

      td {
        padding: 12px 0;

        &:first-child {
          width: 40%;
          color: #64748b;
          padding-right: 20px;
          font-weight: 500;
        }

        &:last-child {
          color: #1e293b;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .report_govem_pdf-viewer-dialog {
    .dialog-content {
      max-height: 80vh;
    }
  }

  .pdf-iframe {
    height: 400px;
  }

  .pdf-toolbar {
    flex-wrap: wrap;

    .page-info {
      order: 3;
      flex-basis: 100%;
      text-align: center;
      margin: 8px 0;
    }
  }

  .report_govem_pdf-info {
    table {
      tr {
        td {
          padding: 10px 0;
          font-size: 12px;
        }
      }
    }
  }
}
</style>
